import React from "react"

import FormErrorMessage from "@components/molecules/FormErrorMessage"

// Simple email regex — server-side already validates presence; this is for UX only.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

const EmailListField = ({
  name = "sales_agent_master[payment_notification_emails]",
  emails = [],
  t = {},
  required = "Required",
  error = null,
}) => {
  // emails can be undefined / "" / [] / string (JSON array) / array. Normalize to array.
  const initial = React.useMemo(() => {
    let parsed: any = emails
    if (typeof parsed === "string") {
      // Try JSON parse first (new format); fall back to newline/comma split (legacy).
      if (parsed.length > 0) {
        try {
          const json = JSON.parse(parsed)
          if (Array.isArray(json)) parsed = json
        } catch (e) {
          parsed = parsed.split(/[,\n]/).map((s) => s.trim())
        }
      } else {
        parsed = []
      }
    }
    if (!Array.isArray(parsed)) return []
    return parsed.map((e) => String(e)).filter((e) => e.length > 0)
  }, [emails])

  // Always keep at least one visible row.
  const [__rows, __rowsSet] = React.useState(
    initial.length > 0 ? initial : [""]
  )

  const handleAdd = () => {
    __rowsSet([...__rows, ""])
  }

  // UX rules:
  //   - 1 row left  -> "-" only clears that row's content (button is disabled if already empty).
  //   - 2+ rows     -> "-" removes the row entirely.
  const handleRemove = (idx) => {
    if (__rows.length === 1) {
      // Last remaining row: clear its content rather than remove the row.
      __rowsSet([""])
      return
    }
    __rowsSet(__rows.filter((_, i) => i !== idx))
  }

  const handleChange = (idx, val) => {
    __rowsSet(__rows.map((row, i) => (i === idx ? val : row)))
  }

  // Pack the visible rows into a single hidden field as a JSON array string.
  // Empty rows are dropped and duplicates removed so the saved value is clean.
  const packedValue = (() => {
    const seen = new Set<string>()
    const cleaned: string[] = []
    for (const r of __rows) {
      const t = r.trim()
      if (t.length > 0 && !seen.has(t)) {
        seen.add(t)
        cleaned.push(t)
      }
    }
    return cleaned.length === 0 ? "" : JSON.stringify(cleaned)
  })()

  return (
    <React.Fragment>
      <input type="hidden" name={name} value={packedValue} />

      {__rows.map((row, idx) => {
        const isLast = __rows.length === 1
        const isEmpty = row.trim() === ""
        // Disabled when this is the last row AND it's already empty (nothing to clear).
        const disabled = isLast && isEmpty
        return (
          <div key={idx} className="EmailListField__row">
            <input
              type="email"
              className="Input"
              value={row}
              placeholder={t.placeholder || "email@example.com"}
              onChange={(e) => handleChange(idx, e.target.value)}
            />
            <button
              type="button"
              className={`Button Button--small Button--icon-minus${
                disabled ? " EmailListField__remove--disabled" : ""
              }`}
              onClick={() => handleRemove(idx)}
              disabled={disabled}
              aria-label="remove"
            >
              −
            </button>
          </div>
        )
      })}

      <div className="EmailListField__add">
        <button
          type="button"
          className="Button Button--small"
          onClick={handleAdd}
        >
          {t.add_button || "Add email"}
        </button>
      </div>

      <FormErrorMessage message={error} />
    </React.Fragment>
  )
}

EmailListField.propTypes = {}

export default EmailListField
